import React, { useState, useEffect } from 'react'
import { 
  Calendar, 
  Clock, 
  BookOpen, 
  Target, 
  TrendingUp, 
  Plus, 
  Search, 
  Filter, 
  Edit3, 
  Trash2, 
  CheckCircle, 
  AlertCircle, 
  BarChart3,
  Users,
  Award,
  Timer,
  MapPin,
  Building,
  X,
  Save
} from 'lucide-react'

// Types
interface TrialExam {
  id: string
  title: string
  description: string
  date: Date
  time: string
  duration: number // dakika
  location: string
  organizer: string
  type: 'planned' | 'completed' | 'missed'
  subjects: {
    turkish: number
    math: number
    science: number
    social: number
    english: number
    religion: number
  }
  totalQuestions: number
  targetScore?: number
  actualScore?: number
  ranking?: number
  totalParticipants?: number
  notes?: string
  isOnline: boolean
  registrationDeadline?: Date
  fee?: number
  difficulty: 'easy' | 'medium' | 'hard'
}

interface ExamStats {
  totalExams: number
  completedExams: number
  averageScore: number
  bestScore: number
  totalStudyTime: number
  upcomingExams: number
  missedExams: number
  improvementRate: number
}

// Constants
const SUBJECTS = [
  { id: 'turkish', name: 'Türkçe', icon: '📚', color: '#ef4444' },
  { id: 'math', name: 'Matematik', icon: '🔢', color: '#3b82f6' },
  { id: 'science', name: 'Fen Bilimleri', icon: '🔬', color: '#10b981' },
  { id: 'social', name: 'Sosyal Bilgiler', icon: '🌍', color: '#f59e0b' },
  { id: 'english', name: 'İngilizce', icon: '🇬🇧', color: '#8b5cf6' },
  { id: 'religion', name: 'Din Kültürü', icon: '☪️', color: '#06b6d4' }
]

const EXAM_TYPES = [
  { value: 'all', label: 'Tüm Sınavlar' },
  { value: 'planned', label: 'Planlanmış' },
  { value: 'completed', label: 'Tamamlanmış' },
  { value: 'missed', label: 'Kaçırılmış' }
]

const DIFFICULTY_LEVELS = [
  { value: 'easy', label: 'Kolay', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' },
  { value: 'medium', label: 'Orta', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' },
  { value: 'hard', label: 'Zor', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' }
]

const TrialExams: React.FC = () => {
  // States
  const [activeTab, setActiveTab] = useState<'exams' | 'calendar' | 'stats'>('exams')
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState('all')
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedExam, setSelectedExam] = useState<TrialExam | null>(null)
  const [exams, setExams] = useState<TrialExam[]>([])
  const [stats, setStats] = useState<ExamStats | null>(null)

  // Demo data
  useEffect(() => {
    const demoExams: TrialExam[] = [
      {
        id: '1',
        title: 'LGS Deneme Sınavı #1',
        description: 'İlk dönem genel değerlendirme sınavı',
        date: new Date('2024-01-15'),
        time: '09:00',
        duration: 120,
        location: 'Okul Konferans Salonu',
        organizer: 'Okul Yönetimi',
        type: 'completed',
        subjects: { turkish: 20, math: 20, science: 20, social: 10, english: 10, religion: 10 },
        totalQuestions: 90,
        targetScore: 85,
        actualScore: 78,
        ranking: 45,
        totalParticipants: 150,
        notes: 'Matematik bölümünde zorlandım',
        isOnline: false,
        difficulty: 'medium'
      },
      {
        id: '2',
        title: 'Online LGS Denemesi',
        description: 'Uzaktan eğitim deneme sınavı',
        date: new Date('2024-01-25'),
        time: '14:00',
        duration: 120,
        location: 'Online Platform',
        organizer: 'Eğitim Kurumu',
        type: 'planned',
        subjects: { turkish: 20, math: 20, science: 20, social: 10, english: 10, religion: 10 },
        totalQuestions: 90,
        targetScore: 90,
        isOnline: true,
        registrationDeadline: new Date('2024-01-23'),
        fee: 25,
        difficulty: 'hard'
      },
      {
        id: '3',
        title: 'Haftalık Değerlendirme',
        description: 'Kısa değerlendirme sınavı',
        date: new Date('2024-01-10'),
        time: '10:00',
        duration: 60,
        location: 'Sınıf 8-A',
        organizer: 'Sınıf Öğretmeni',
        type: 'missed',
        subjects: { turkish: 10, math: 10, science: 10, social: 5, english: 5, religion: 5 },
        totalQuestions: 45,
        targetScore: 80,
        notes: 'Hasta olduğum için katılamadım',
        isOnline: false,
        difficulty: 'easy'
      }
    ]

    setExams(demoExams)

    // Calculate stats
    const completedExams = demoExams.filter(e => e.type === 'completed')
    const totalScore = completedExams.reduce((sum, exam) => sum + (exam.actualScore || 0), 0)
    const avgScore = completedExams.length > 0 ? totalScore / completedExams.length : 0

    setStats({
      totalExams: demoExams.length,
      completedExams: completedExams.length,
      averageScore: Math.round(avgScore),
      bestScore: Math.max(...completedExams.map(e => e.actualScore || 0), 0),
      totalStudyTime: completedExams.reduce((sum, exam) => sum + exam.duration, 0),
      upcomingExams: demoExams.filter(e => e.type === 'planned').length,
      missedExams: demoExams.filter(e => e.type === 'missed').length,
      improvementRate: 12
    })
  }, [])

  // Filtered exams
  const filteredExams = exams.filter(exam => {
    const matchesSearch = exam.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         exam.description.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesFilter = filterType === 'all' || exam.type === filterType
    return matchesSearch && matchesFilter
  })

  // Helper functions
  const getTypeColor = (type: string) => {
    switch (type) {
      case 'planned': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
      case 'completed': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
      case 'missed': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'
    }
  }

  const getTypeText = (type: string) => {
    switch (type) {
      case 'planned': return 'Planlanmış'
      case 'completed': return 'Tamamlanmış'
      case 'missed': return 'Kaçırılmış'
      default: return 'Bilinmiyor'
    }
  }

  const getDifficultyColor = (difficulty: string) => {
    return DIFFICULTY_LEVELS.find(d => d.value === difficulty)?.color || 'bg-gray-100 text-gray-800'
  }

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    })
  }

  const isUpcoming = (date: Date) => {
    return date > new Date()
  }

  const addExam = (examData: Partial<TrialExam>) => {
    const newExam: TrialExam = {
      id: Date.now().toString(),
      title: examData.title || '',
      description: examData.description || '',
      date: examData.date || new Date(),
      time: examData.time || '09:00',
      duration: examData.duration || 120,
      location: examData.location || '',
      organizer: examData.organizer || '',
      type: 'planned',
      subjects: examData.subjects || { turkish: 20, math: 20, science: 20, social: 10, english: 10, religion: 10 },
      totalQuestions: examData.totalQuestions || 90,
      targetScore: examData.targetScore,
      isOnline: examData.isOnline || false,
      registrationDeadline: examData.registrationDeadline,
      fee: examData.fee,
      difficulty: examData.difficulty || 'medium',
      notes: examData.notes
    }

    setExams(prev => [...prev, newExam])
    setShowAddModal(false)
  }

  const updateExam = (examData: Partial<TrialExam>) => {
    if (!selectedExam) return

    setExams(prev => prev.map(exam => 
      exam.id === selectedExam.id 
        ? { ...exam, ...examData }
        : exam
    ))
    setShowEditModal(false)
    setSelectedExam(null)
  }

  const deleteExam = (examId: string) => {
    setExams(prev => prev.filter(exam => exam.id !== examId))
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Deneme Sınavları
          </h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
            Deneme sınavlarınızı planlayın, takip edin ve sonuçlarınızı analiz edin
          </p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <BookOpen className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {stats.totalExams}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Toplam Sınav
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {stats.completedExams}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Tamamlanan
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                  <Target className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {stats.averageScore}%
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Ortalama Puan
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                  <Calendar className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {stats.upcomingExams}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Yaklaşan Sınav
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-1 mb-4 sm:mb-6 bg-white dark:bg-gray-800 rounded-lg p-1 border border-gray-200 dark:border-gray-700">
          {[
            { id: 'exams', label: 'Sınavlar', icon: BookOpen },
            { id: 'calendar', label: 'Takvim', icon: Calendar },
            { id: 'stats', label: 'İstatistikler', icon: BarChart3 }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center justify-center sm:justify-start gap-2 px-3 sm:px-4 py-2 rounded-lg transition-colors text-sm sm:text-base ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span className="hidden sm:inline">{tab.label}</span>
              <span className="sm:hidden">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Exams Tab */}
        {activeTab === 'exams' && (
          <div className="space-y-6">
            {/* Controls */}
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <div className="flex flex-col sm:flex-row gap-3 flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Sınav ara..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 w-full sm:w-64"
                  />
                </div>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                >
                  {EXAM_TYPES.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Yeni Sınav Ekle
              </button>
            </div>

            {/* Exams List */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {filteredExams.map(exam => (
                <div
                  key={exam.id}
                  className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                        {exam.title}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        {exam.description}
                      </p>
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(exam.type)}`}>
                          {getTypeText(exam.type)}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(exam.difficulty)}`}>
                          {DIFFICULTY_LEVELS.find(d => d.value === exam.difficulty)?.label}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          setSelectedExam(exam)
                          setShowEditModal(true)
                        }}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteExam(exam.id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(exam.date)}
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {exam.time}
                      </div>
                      <div className="flex items-center gap-1">
                        <Timer className="w-4 h-4" />
                        {exam.duration} dk
                      </div>
                    </div>

                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                      <MapPin className="w-4 h-4" />
                      {exam.location}
                      {exam.isOnline && (
                        <span className="px-2 py-1 bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 rounded-full text-xs">
                          Online
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                      <Building className="w-4 h-4" />
                      {exam.organizer}
                    </div>

                    {exam.type === 'completed' && exam.actualScore && (
                      <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Award className="w-4 h-4 text-green-600 dark:text-green-400" />
                          <span className="text-sm font-medium text-green-800 dark:text-green-300">
                            Puan: {exam.actualScore}%
                          </span>
                        </div>
                        {exam.ranking && (
                          <div className="flex items-center gap-1 text-sm text-green-700 dark:text-green-400">
                            <Users className="w-4 h-4" />
                            {exam.ranking}/{exam.totalParticipants}
                          </div>
                        )}
                      </div>
                    )}

                    {exam.type === 'planned' && exam.targetScore && (
                      <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <Target className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        <span className="text-sm font-medium text-blue-800 dark:text-blue-300">
                          Hedef: {exam.targetScore}%
                        </span>
                      </div>
                    )}

                    {exam.notes && (
                      <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          {exam.notes}
                        </p>
                      </div>
                    )}

                    {exam.registrationDeadline && isUpcoming(exam.registrationDeadline) && (
                      <div className="flex items-center gap-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                        <AlertCircle className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                        <span className="text-sm text-yellow-800 dark:text-yellow-300">
                          Kayıt son tarihi: {formatDate(exam.registrationDeadline)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {filteredExams.length === 0 && (
              <div className="text-center py-12">
                <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  Henüz sınav bulunmuyor
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  İlk deneme sınavınızı ekleyerek başlayın
                </p>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Sınav Ekle
                </button>
              </div>
            )}
          </div>
        )}

        {/* Calendar Tab */}
        {activeTab === 'calendar' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Sınav Takvimi
            </h2>
            <div className="text-center py-12">
              <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">
                Takvim görünümü yakında eklenecek
              </p>
            </div>
          </div>
        )}

        {/* Stats Tab */}
        {activeTab === 'stats' && stats && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Genel İstatistikler
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">En Yüksek Puan:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{stats.bestScore}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Toplam Sınav Süresi:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{stats.totalStudyTime} dakika</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Kaçırılan Sınav:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{stats.missedExams}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Gelişim Oranı:</span>
                    <span className="font-medium text-green-600 dark:text-green-400">+{stats.improvementRate}%</span>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Performans Analizi
                </h3>
                <div className="text-center py-8">
                  <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">
                    Detaylı analiz grafikleri yakında eklenecek
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <ExamModal
          exam={selectedExam}
          isEdit={showEditModal}
          onSave={showEditModal ? updateExam : addExam}
          onClose={() => {
            setShowAddModal(false)
            setShowEditModal(false)
            setSelectedExam(null)
          }}
        />
      )}
    </div>
  )
}

// Exam Modal Component
interface ExamModalProps {
  exam?: TrialExam | null
  isEdit: boolean
  onSave: (exam: Partial<TrialExam>) => void
  onClose: () => void
}

const ExamModal: React.FC<ExamModalProps> = ({ exam, isEdit, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    title: exam?.title || '',
    description: exam?.description || '',
    date: exam?.date ? exam.date.toISOString().split('T')[0] : '',
    time: exam?.time || '09:00',
    duration: exam?.duration || 120,
    location: exam?.location || '',
    organizer: exam?.organizer || '',
    targetScore: exam?.targetScore || '',
    isOnline: exam?.isOnline || false,
    difficulty: exam?.difficulty || 'medium',
    notes: exam?.notes || '',
    fee: exam?.fee || ''
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSave({
      ...formData,
      date: new Date(formData.date),
      targetScore: formData.targetScore ? Number(formData.targetScore) : undefined,
      fee: formData.fee ? Number(formData.fee) : undefined
    })
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isEdit ? 'Sınavı Düzenle' : 'Yeni Sınav Ekle'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Sınav Adı *
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Zorluk Seviyesi
                </label>
                <select
                  value={formData.difficulty}
                  onChange={(e) => setFormData(prev => ({ ...prev, difficulty: e.target.value as any }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                >
                  {DIFFICULTY_LEVELS.map(level => (
                    <option key={level.value} value={level.value}>
                      {level.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Açıklama
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tarih *
                </label>
                <input
                  type="date"
                  required
                  value={formData.date}
                  onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Saat *
                </label>
                <input
                  type="time"
                  required
                  value={formData.time}
                  onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Süre (dakika) *
                </label>
                <input
                  type="number"
                  required
                  min="1"
                  value={formData.duration}
                  onChange={(e) => setFormData(prev => ({ ...prev, duration: Number(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Konum *
                </label>
                <input
                  type="text"
                  required
                  value={formData.location}
                  onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Düzenleyen *
                </label>
                <input
                  type="text"
                  required
                  value={formData.organizer}
                  onChange={(e) => setFormData(prev => ({ ...prev, organizer: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Hedef Puan (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.targetScore}
                  onChange={(e) => setFormData(prev => ({ ...prev, targetScore: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Ücret (₺)
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.fee}
                  onChange={(e) => setFormData(prev => ({ ...prev, fee: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.isOnline}
                  onChange={(e) => setFormData(prev => ({ ...prev, isOnline: e.target.checked }))}
                  className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Online sınav
                </span>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Notlar
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                İptal
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Save className="w-4 h-4 inline mr-2" />
                {isEdit ? 'Güncelle' : 'Kaydet'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default TrialExams